import { useAuth } from '@context/AuthContext';
import useModules from '@utils/hooks/useModules';
import { ModuleName } from '@utils/constants';

// Multisucursal — gate único de "puede ver/operar pedidos de OTRAS sucursales".
//
// Reglas de negocio (autoritativas):
//   1a) Usuario ROOT de un comercio con el módulo PEDIDOS_MULTISUCURSAL habilitado.
//   1b) Usuario NO-ROOT que tiene el permiso PEDIDOS_MULTISUCURSAL asignado (user_modules,
//       vía AddUser) y pertenece a un comercio con el módulo habilitado.
//   3)  Usuario en un comercio con el módulo pero SIN el permiso → NO se entera de otras
//       sucursales (lo gestiona shouldIgnoreIncomingOrder cuando este gate es `false`).
//
// Antes este predicado estaba duplicado como `isRoot && hasModule(PEDIDOS_MULTISUCURSAL)` en
// useIncomingOrder, useCanSwitchBranch y useBranchSessionGuard. Ese `isRoot &&` excluía
// incorrectamente a los usuarios no-root con permiso (regla 1b). Centralizado aquí para que
// un cambio de regla toque un solo archivo.
//
// Anti-regresión #1004: este gate NUNCA se aplica a los pedidos PROPIOS — el pedido propio pasa
// siempre, independientemente de este valor.
//
// ⚠️ DEPENDENCIAS DE BACKEND PENDIENTES (regla 1b para no-root) — confirmar antes de habilitarlo
// en producción para no-root:
//   OQ-1: ¿`/v1/auth/account-details` devuelve `modulesAndPermissions` por-comercio (igual para
//         todos) o ya filtrado por-usuario? Define si `hasModule()` ya codifica el grant del no-root.
//   OQ-2: ¿El gate L1 del backend que llena `accountCommerceTokens` filtra por `isRoot` o por el
//         permiso del usuario? Si filtra por `isRoot`, un no-root con permiso verá "Ver" pero al
//         tocarlo `refreshLogin` aborta (token vacío, AuthContext) → "Ver" no-op. Sería fix de backend.
//   OQ-3: ¿El login devuelve `modulos` (→ `user_modules`) confiablemente para no-root con el permiso?
//         Si viene vacío, `hasUserModule` da `false` y el no-root sigue bloqueado en silencio.

// Predicado puro (testeable sin render).
export const computeCanSeeOtherBranchOrders = (
  isRoot: boolean,
  commerceHasModule: boolean,
  userHasModule: boolean,
): boolean => {
  if (!commerceHasModule) return false;
  return isRoot || userHasModule;
};

export const useMultisucursalGate = (): boolean => {
  const { isRoot, hasUserModule } = useAuth();
  const { hasModule } = useModules();

  return computeCanSeeOtherBranchOrders(
    isRoot,
    hasModule(ModuleName.PEDIDOS_MULTISUCURSAL) === true,
    hasUserModule(ModuleName.PEDIDOS_MULTISUCURSAL),
  );
};

export default useMultisucursalGate;
